import {
	EnvironmentFeature,
	getRequiredUserLicenseForEnvironmentFeature
} from "client/Features/FeaturePackages/misc";
import {
	AvailableNotificationViewModel,
	NotificationKind
} from "client/Api/Notifications/notifications";
import { isNil } from "client/Misc/utils";
import { userConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { UserLicense } from "client/Api/FeaturePackages/featurePackageManagement";
import { AppType } from "client/Api/Apps/common";
import { never } from "client_legacy/ndaw/errors";
import { doesUserLicenseSatisfyRequiredLicense } from "client/Hooks";

export function hasRequiredLicenseForNotification(
	notification: AvailableNotificationViewModel
): boolean {
	const environmentFeature: EnvironmentFeature | null = getEnvironmentFeatureForNotification(notification);

	if (isNil(environmentFeature)) {
		return true;
	}

	const { userLicense } = userConfiguration();
	const requiredUserLicense: UserLicense = getRequiredUserLicenseForEnvironmentFeature(environmentFeature);

	return doesUserLicenseSatisfyRequiredLicense(userLicense, requiredUserLicense);
}

function getEnvironmentFeatureForNotification(
	notification: AvailableNotificationViewModel
): EnvironmentFeature | null {
	switch(notification.kind) {
		case NotificationKind.Announcement:
		case NotificationKind.AnnouncementComment:
		case NotificationKind.AnnouncementCommentMention:
		case NotificationKind.AnnouncementReaction:
		case NotificationKind.KnowledgeItem:
		case NotificationKind.Event:
		case NotificationKind.EventAttendance:
		case NotificationKind.AnnouncementCommentReaction:
		case NotificationKind.CommunityPost:
		case NotificationKind.CommunityPostComment:
		case NotificationKind.CommunityPostCommentMention:
		case NotificationKind.Community:
		case NotificationKind.CommunityMembership:
		case NotificationKind.CommunityPostCommentReaction:
		case NotificationKind.CommunityPostReaction:
		case NotificationKind.EventReaction:
		case NotificationKind.EventComment:
		case NotificationKind.EventCommentMention:
		case NotificationKind.EventCommentReaction:
		case NotificationKind.KnowledgeItemComment:
		case NotificationKind.KnowledgeItemCommentReaction:
		case NotificationKind.KnowledgeItemCommentMention:
		case NotificationKind.KnowledgeItemReaction:
			return EnvironmentFeature.Hub;
		case NotificationKind.Birthday:
		case NotificationKind.BirthdayWishReaction:
		case NotificationKind.BirthdayWish:
			return EnvironmentFeature.Birthdays;
		case NotificationKind.AppAccess:
		case NotificationKind.Maintenance:
		case NotificationKind.NewMainAppPromotion:
			return getEnvironmentFeatureForAppType(notification.data
				.appType
			);
		case NotificationKind.CrossStoreOperation:
		case NotificationKind.Custom:
		case NotificationKind.DocumentShared:
		case NotificationKind.NewEmail:
		case NotificationKind.UserRequest:
		case NotificationKind.Unknown:
			return null;
		default:
			return never(notification);
	}
}

function getEnvironmentFeatureForAppType(
	appType: AppType
): EnvironmentFeature | null {
	switch(appType) {
		case AppType.CitrixXenApp:
			return EnvironmentFeature.CitrixXenApp;
		case AppType.ClientlessRdp:
			return EnvironmentFeature.ClientlessRdpApp;
		case AppType.WindowsVirtualDesktop:
			return EnvironmentFeature.AzureVirtualDesktopApp;
		case AppType.VmwareHorizonApp:
			return EnvironmentFeature.VmwareHorizonApp;
		case AppType.Liquit:
			return EnvironmentFeature.Liquit;
		case AppType.Hub:
			return EnvironmentFeature.Hub;
		case AppType.Teams:
			return EnvironmentFeature.MicrosoftTeamsApp;
		case AppType.Rss:
			return EnvironmentFeature.RssApp;
		case AppType.Birthday:
			return EnvironmentFeature.Birthdays;
		case AppType.ToDo:
			return EnvironmentFeature.MicrosoftToDoApp;
		case AppType.SharePoint:
			return EnvironmentFeature.SharePointApp;
		case AppType.AddressBook:
			return EnvironmentFeature.AddressBook;
		case AppType.YouTube:
			return EnvironmentFeature.YoutubeApp;
		case AppType.Afas:
			return EnvironmentFeature.Afas;
		case AppType.ZenyaDoc:
			return EnvironmentFeature.ZenyaDoc;
		case AppType.VilansKick:
			return EnvironmentFeature.VilansKick;
		case AppType.Autotask:
			return EnvironmentFeature.Autotask;
		case AppType.Zaaksysteem:
			return EnvironmentFeature.Zaaksysteem;
		case AppType.Zermelo:
			return EnvironmentFeature.Zermelo;
		case AppType.TopDesk:
			return EnvironmentFeature.TopDesk;
		case AppType.NedapOns:
			return EnvironmentFeature.NedapOns;
		case AppType.Magister:
			return EnvironmentFeature.Magister;
		case AppType.Yammer:
			return EnvironmentFeature.VivaEngageApp;
		case AppType.Integration:
			return EnvironmentFeature.IntegrationFramework;
		case AppType.AzureApp:
		case AppType.ConnectWise:
		case AppType.Documents:
		case AppType.Email:
		case AppType.Embed:
		case AppType.Google:
		case AppType.HowTo:
		case AppType.LocalApp:
		case AppType.Office365Calendar:
		case AppType.Office365Excel:
		case AppType.Office365LyncMeeting:
		case AppType.Office365OneNote:
		case AppType.Office365PeopleScreen:
		case AppType.Office365PowerPoint:
		case AppType.Office365Word:
		case AppType.PowerBi:
		case AppType.Unknown:
		case AppType.Url:
			return null;
		default:
			return never(appType);
	}
}